'use client'

import { useRef, useState, useEffect } from 'react'
import { cn } from '@/utilities/ui'

interface PixelatedImageProps {
  src: string
  alt: string
  className?: string
  aspectRatio?: '3/4' | '4/3' | '5/4' | '2/1'
  pixelSize?: number
  defaultState?: 'pixelated' | 'original'
  isHovering?: boolean
}

export const PixelatedImage: React.FC<PixelatedImageProps> = ({
  src,
  alt,
  className,
  aspectRatio,
  pixelSize = 24,
  defaultState = 'original',
  isHovering: externalHovering,
}) => {
  const [internalHovering, setInternalHovering] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Use external hovering state if provided, otherwise use internal state
  const isHovering = externalHovering !== undefined ? externalHovering : internalHovering

  // Create pixelated version once the image is loaded
  const createPixelatedVersion = () => {
    const canvas = canvasRef.current
    const container = containerRef.current
    const originalImg = imgRef.current
    if (!canvas || !container || !originalImg) return

    // Get container dimensions
    const rect = container.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Calculate how to draw the image with object-cover semantics
    const imgWidth = originalImg.naturalWidth
    const imgHeight = originalImg.naturalHeight

    const containerWidth = rect.width
    const containerHeight = rect.height

    const containerAspect = containerWidth / containerHeight
    const imgAspect = imgWidth / imgHeight

    let drawWidth: number, drawHeight: number
    let offsetX = 0,
      offsetY = 0

    // Match object-cover behavior
    if (containerAspect > imgAspect) {
      // Container is wider than image (relative to height)
      drawWidth = containerWidth
      drawHeight = containerWidth / imgAspect
      offsetY = (containerHeight - drawHeight) / 2
    } else {
      // Container is taller than image (relative to width)
      drawHeight = containerHeight
      drawWidth = containerHeight * imgAspect
      offsetX = (containerWidth - drawWidth) / 2
    }

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // First draw the image exactly as it would be displayed with object-cover
    ctx.drawImage(originalImg, offsetX, offsetY, drawWidth, drawHeight)

    // Get the image data from what we've drawn (only the visible portion)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data

    // Create a new array to hold pixelated data
    const pixelatedData = new Uint8ClampedArray(data.length)

    // Process each block for pixelation
    for (let y = 0; y < canvas.height; y += pixelSize) {
      for (let x = 0; x < canvas.width; x += pixelSize) {
        // Calculate block size (handle edge cases)
        const blockW = Math.min(pixelSize, canvas.width - x)
        const blockH = Math.min(pixelSize, canvas.height - y)

        // Calculate average color for this block
        let r = 0,
          g = 0,
          b = 0,
          a = 0,
          count = 0

        // Sum all pixels in this block
        for (let by = 0; by < blockH; by++) {
          for (let bx = 0; bx < blockW; bx++) {
            const px = x + bx
            const py = y + by
            const i = (py * canvas.width + px) * 4

            if (i < data.length) {
              r += data[i] || 0
              g += data[i + 1] || 0
              b += data[i + 2] || 0
              a += data[i + 3] || 0
              count++
            }
          }
        }

        if (count > 0) {
          // Calculate average
          r = Math.round(r / count)
          g = Math.round(g / count)
          b = Math.round(b / count)
          a = Math.round(a / count)

          // Fill block with average color
          for (let by = 0; by < blockH; by++) {
            for (let bx = 0; bx < blockW; bx++) {
              const px = x + bx
              const py = y + by
              const i = (py * canvas.width + px) * 4

              if (i < pixelatedData.length) {
                pixelatedData[i] = r
                pixelatedData[i + 1] = g
                pixelatedData[i + 2] = b
                pixelatedData[i + 3] = a
              }
            }
          }
        }
      }
    }

    // Put pixelated data back to canvas
    const pixelatedImageData = new ImageData(pixelatedData, canvas.width, canvas.height)
    ctx.putImageData(pixelatedImageData, 0, 0)

    setImageLoaded(true)
  }

  // Process image when the original image is loaded
  useEffect(() => {
    if (imgRef.current?.complete) {
      // Already loaded
      createPixelatedVersion()
    } else {
      // Wait for load
      const handleImageLoad = () => {
        createPixelatedVersion()
      }

      const imgElement = imgRef.current
      if (imgElement) {
        imgElement.addEventListener('load', handleImageLoad)
        return () => imgElement.removeEventListener('load', handleImageLoad)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, pixelSize])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current) {
        setImageLoaded(false)
        setTimeout(createPixelatedVersion, 100) // Delay to ensure DOM has updated
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelSize])

  // Determine when to show the pixelated version
  const showPixelated = defaultState === 'pixelated' ? !isHovering : isHovering

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-hidden',
        'transform transition-transform duration-300 group-hover:scale-105',
        aspectRatio === '3/4' && 'aspect-[3/4]',
        aspectRatio === '4/3' && 'aspect-[4/3]',
        aspectRatio === '5/4' && 'aspect-[5/4]',
        aspectRatio === '2/1' && 'aspect-[2/1]',
        className,
      )}
      onMouseEnter={() => setInternalHovering(true)}
      onMouseLeave={() => setInternalHovering(false)}
    >
      {/* Original image (always visible) */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Pixelated overlay (opacity controlled by hover) */}
      <canvas
        ref={canvasRef}
        className={cn(
          'absolute inset-0 w-full h-full transition-opacity duration-200',
          imageLoaded && showPixelated ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}
